import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './DeliveryDetail.module.css';

// Toast 컴포넌트
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  isVisible: boolean;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // 3초로 수정
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const getToastStyle = () => {
    const baseStyle = {
      position: 'fixed' as const,
      top: '50%', // bottom에서 top으로 변경
      left: '50%', // 유지
      transform: 'translate(-50%, -50%)', // 중앙 정렬을 위한 transform 수정
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: 'bold',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-out', // 애니메이션 이름 변경
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    };

    switch (type) {
      case 'success':
        return { ...baseStyle, backgroundColor: '#4caf50' };
      case 'error':
        return { ...baseStyle, backgroundColor: '#f44336' };
      case 'info':
        return { ...baseStyle, backgroundColor: '#2196f3' };
      default:
        return { ...baseStyle, backgroundColor: '#757575' };
    }
  };

  return (
    <div style={getToastStyle()}>
      {message}
    </div>
  );
};

interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  calledName: string;
  phone_number: string;
  phone_number_verified: boolean;
  agree_terms_of_service: boolean;
  agree_privacy_policy: boolean;
  agree_marketing_information: boolean;
  provider: string;
  groups: string[];
  roles: string[];
  is_admin: boolean;
  is_system: boolean;
  is_manager: boolean;
  is_user: boolean;
  orders: string[];
  subscriptions: string[];
  free_addum_days: number;
}

interface PhysicalBookOrder {
  id: number;
  project_id: number;
  book_id: number;
  created_at: string;
  updated_at: string;
  postal_code: string;
  address: string;
  address_detail: string | null;
  recipient_name: string;
  recipient_phone: string;
  delivery_state: string;
  delivery_state_label: string;
  cover_style_id: string;
  cover_style_name: string | null;
  cover_image_url: string | null;
  admin_note: string | null;
  project_title: string | null;
  book_status: string | null;
  user_id: string | null;
  customer_name?: string;
  user?: User;
}

interface DeliveryState {
  value: string;
  label: string;
}

const DeliveryDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getDeliveryDetail, updateDeliveryStatus, cancelDelivery, downloadBookContents, deletePhysicalBookOrder } = useApi();
  const [order, setOrder] = useState<PhysicalBookOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  
  // 상태 변경 관련 상태
  const [newDeliveryState, setNewDeliveryState] = useState<string>('');
  const [adminNote, setAdminNote] = useState<string>('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState<string>('');
  const [deletePasscodeInput, setDeletePasscodeInput] = useState<string>('');
  const [isDeletePasscodeVerified, setIsDeletePasscodeVerified] = useState<boolean>(false);
  
  // 주문 취소 인증 관련 상태
  const [cancelPasscode, setCancelPasscode] = useState<string>('');
  const [cancelPasscodeInput, setCancelPasscodeInput] = useState<string>('');
  const [isPasscodeVerified, setIsPasscodeVerified] = useState<boolean>(false);

  // Toast 상태
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [toastIsVisible, setToastIsVisible] = useState(false);

  // 배송 상태 옵션
  const deliveryStates: DeliveryState[] = [
    { value: 'ORDERED', label: '실물책 편집' },
    { value: 'PRINTING', label: '인쇄' },
    { value: 'SHIPPED', label: '배송' },
    { value: 'DELIVERED', label: '배송완료' },
    { value: 'CANCELLED', label: '주문취소' }
  ];

  // 실물책 주문 상세 정보 조회
  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getDeliveryDetail(orderId!);
      setOrder(data);
      setNewDeliveryState(data.delivery_state);
      setAdminNote(data.admin_note || '');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (orderId) {
      fetchOrderDetail();
    }
  }, [orderId]);

  // 배송 상태 변경
  const handleStatusChange = async () => {
    if (!order || !newDeliveryState) return;
    
    try {
      setUpdating(true);
      
      const result = await updateDeliveryStatus(orderId!, newDeliveryState, adminNote || undefined);
      setToastMessage(result.message);
      setToastType('success');
      setToastIsVisible(true);
      
      // 상태 변경 후 데이터 다시 로드
      await fetchOrderDetail();
      setShowStatusModal(false);
      
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : '상태 변경 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setUpdating(false);
    }
  };

  // 랜덤 패스코드 생성 (6자리 숫자)
  const generateRandomPasscode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // 주문 취소 모달 열기 시 패스코드 생성
  const handleOpenCancelModal = () => {
    const newPasscode = generateRandomPasscode();
    setCancelPasscode(newPasscode);
    setCancelPasscodeInput('');
    setIsPasscodeVerified(false);
    setShowCancelModal(true);
  };

  // 패스코드 인증 확인
  const handleVerifyPasscode = () => {
    if (cancelPasscodeInput === cancelPasscode) {
      setIsPasscodeVerified(true);
      setToastMessage('패스코드가 확인되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
    } else {
      setIsPasscodeVerified(false);
      setToastMessage('패스코드가 일치하지 않습니다.');
      setToastType('error');
      setToastIsVisible(true);
    }
  };

  // 주문 취소
  const handleCancelOrder = async () => {
    if (!order || !isPasscodeVerified) return;
    
    try {
      setUpdating(true);
      
      const result = await cancelDelivery(orderId!, adminNote || undefined);
      setToastMessage(result.message);
      setToastType('success');
      setToastIsVisible(true);
      
      // 취소 후 데이터 다시 로드
      await fetchOrderDetail();
      setShowCancelModal(false);
      
      // 인증 상태 초기화
      setIsPasscodeVerified(false);
      setCancelPasscodeInput('');
      
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : '주문 취소 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setUpdating(false);
    }
  };

  // 삭제 모달 열기
  const handleOpenDeleteModal = () => {
    const newPasscode = generateRandomPasscode();
    setDeletePasscode(newPasscode);
    setDeletePasscodeInput('');
    setIsDeletePasscodeVerified(false);
    setShowDeleteModal(true);
  };

  // 삭제 패스코드 검증
  const handleVerifyDeletePasscode = () => {
    if (deletePasscodeInput === deletePasscode) {
      setIsDeletePasscodeVerified(true);
      setToastMessage('패스코드가 확인되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
    } else {
      setIsDeletePasscodeVerified(false);
      setToastMessage('패스코드가 일치하지 않습니다.');
      setToastType('error');
      setToastIsVisible(true);
    }
  };

  // 주문 삭제
  const handleDeleteDelivery = async () => {
    if (!order || !isDeletePasscodeVerified) {
      setToastMessage('패스코드 인증이 필요합니다.');
      setToastType('error');
      setToastIsVisible(true);
      return;
    }
    
    try {
      setUpdating(true);
      
      await deletePhysicalBookOrder(order.project_id, order.book_id);
      setToastMessage('주문이 성공적으로 삭제되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
      
      // 삭제 후 목록 페이지로 이동
      navigate('/admin/delivery');
      
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : '주문 삭제 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setUpdating(false);
      setShowDeleteModal(false);
      setIsDeletePasscodeVerified(false);
      setDeletePasscodeInput('');
    }
  };

  // 배송 상태별 배경색 반환
  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'ORDERED': return '#e3f2fd';
      case 'PRINTING': return '#fff3e0';
      case 'SHIPPED': return '#e8f5e8';
      case 'DELIVERED': return '#e8f5e8';
      case 'CANCELLED': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  // 배송 상태별 텍스트 색상 반환
  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'ORDERED': return '#1976d2';
      case 'PRINTING': return '#f57c00';
      case 'SHIPPED': return '#388e3c';
      case 'DELIVERED': return '#388e3c';
      case 'CANCELLED': return '#d32f2f';
      default: return '#757575';
    }
  };

  // 실물책 컨텐츠 다운로드 (ZIP 파일로 다운로드)
  const handleDownloadEbook = async () => {
    if (!order) return;
    
    try {
      setUpdating(true);
      
      // ApiProvider의 downloadBookContents API 사용
      const blob = await downloadBookContents(order.project_id, order.book_id, true);
      
      // 파일명 생성 (서버 규칙과 동일한 기본값)
      const filename = `p${order.project_id}b${order.book_id}po${order.id}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '')}.zip`;
      
      // 파일 다운로드 처리
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setToastMessage('실물책 컨텐츠가 성공적으로 다운로드되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
      
    } catch (err) {
      console.error('실물책 컨텐츠 다운로드 실패:', err);
      setToastMessage(err instanceof Error ? err.message : '다운로드 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setUpdating(false);
    }
  };

  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    if (fromPage && fromPage !== '/admin/delivery') {
      // 다른 페이지에서 온 경우 해당 페이지로 이동
      navigate(fromPage);
    } else {
      // 배송 목록에서 온 경우 또는 기본값
      navigate('/admin/delivery');
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== '/admin/delivery') {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };

  if (loading) {
    return (
      <div className={styles.deliveryDetailContainer}>
        <h1>실물책 주문 상세정보</h1>
        <div className={styles.loading}>
          <div style={{ fontSize: '24px', marginBottom: '20px' }}>📦</div>
          <div>실물책 주문 정보를 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className={styles.deliveryDetailContainer}>
        <h1>실물책 주문 상세정보</h1>
        <div className={styles.error}>
          <div style={{ fontSize: '24px', marginBottom: '20px' }}>❌</div>
          <div>오류: {error || '주문 정보를 찾을 수 없습니다.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.deliveryDetailContainer}>
      <div className={styles.pageTitleRow}>
        <h1>실물책 주문 상세정보</h1>
        <button 
          onClick={handleGoBack} 
          className={styles.backBtn}
        >
          {getBackButtonText()}
        </button>
      </div>
      
      <div className={styles.deliveryDetailContent}>
        {/* 왼쪽 파티션 (3/4) */}
        <div className={styles.leftPartition}>
          {/* 기본 정보 섹션 */}
          <div className={styles.infoSection}>
            <h2>📋 기본 정보</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>주문 ID</label>
                <span>#{order.id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>프로젝트 ID</label>
                <span>#{order.project_id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>책 ID</label>
                <span>#{order.book_id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>주문자 성명</label>
                <span>{order.recipient_name}</span>
              </div>
              {/* <div className={styles.infoItem}>
                <label>책 상태</label>
                <span>{order.book_status || '상태 없음'}</span>
              </div> */}
              <div className={styles.infoItem}>
                <label>관리상태</label>
                <span style={{ 
                  color: order.delivery_state === 'CANCELLED' ? '#dc3545' : '#28a745',
                  fontWeight: 'bold'
                }}>
                  {order.delivery_state === 'CANCELLED' ? '❌ 취소됨' : '✅ 활성'}
                </span>
              </div>

              <div className={styles.infoItem}>
                    <label>현재 상태</label>
                    <span
                      // className="status-badge large"
                      style={{
                        backgroundColor: getStatusBackgroundColor(order.delivery_state),
                        color: getStatusTextColor(order.delivery_state)
                      }}
                    >
                      {order.delivery_state_label}
                    </span>
                  </div>
            </div>
          </div>

          {/* 주문자 정보 섹션 */}
          {order.user && (
            <div className={styles.infoSection}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2>👤 주문자 정보</h2>
                <button
                  onClick={() => {
                    const targetPath = `/admin/users/detail/${order.user!.id}`;
                    try {
                      navigate(targetPath);
                    } catch (error) {
                      window.location.href = targetPath;
                    }
                  }}
                  className={styles.userInfoLink}
                >
                  👤 사용자 정보보기
                </button>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>사용자명</label>
                  <span>{order.user.username}</span>
                </div>
                <div className={styles.infoItem}>
                  <label>실명</label>
                  <span>{order.user.name}</span>
                </div>
                <div className={styles.infoItem}>
                  <label>이메일</label>
                  <span>
                    <a href={`mailto:${order.user.email}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                      {order.user.email}
                    </a>
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <label>연락처</label>
                  <span>
                    <a href={`tel:${order.user.phone_number}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                      {order.user.phone_number}
                    </a>
                    {order.user.phone_number_verified && (
                      <span style={{ color: '#28a745', marginLeft: '5px' }}>✓</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <label>구독 정보</label>
                  <span>
                    {order.user.subscriptions && order.user.subscriptions.length > 0 ? (
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                        구독 중 ({order.user.subscriptions.length}개)
                      </span>
                    ) : (
                      <span style={{ color: '#6c757d' }}>구독 없음</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 배송지, 주문정보, 처리시각을 한 행에 배치 */}
          <div className={styles.infoRow}>
            {/* 배송지 섹션 */}
            <div className={styles.infoSection}>
              <h2>🚚 배송지</h2>
              <div className={styles.infoList}>
                <div className={styles.infoListItem}>
                  <label>수령인</label>
                  <span>{order.recipient_name}</span>
                </div>
                <div className={styles.infoListItem}>
                  <label>연락처</label>
                  <span>{order.recipient_phone}</span>
                </div>
                <div className={styles.infoListItem}>
                  <label>우편번호</label>
                  <span>{order.postal_code}</span>
                </div>
                <div className={styles.infoListItem}>
                  <label>주소</label>
                  <span>{order.address}</span>
                </div>
                {order.address_detail && (
                  <div className={styles.infoListItem}>
                    <label>상세주소</label>
                    <span>{order.address_detail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 주문 정보 섹션 */}
            <div className={styles.infoSection}>
              <h2>📅 주문 정보</h2>
              <div className={styles.infoList}>
                <div className={styles.infoListItem}>
                  <label>커버 이미지</label>
                  <span>
                    {order.cover_image_url ? (
                      <div className={styles.coverImageContainer}>
                        <img 
                          src={order.cover_image_url} 
                          alt="커버 이미지" 
                          className={styles.coverImagePreview}
                        />
                        <a 
                          href={order.cover_image_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.coverImageLink}
                        >
                          🔗 새 창에서 보기
                        </a>
                      </div>
                    ) : (
                      '이미지 없음'
                    )}
                  </span>
                </div>
                <div className={styles.infoListItem}>
                  <label>커버 스타일</label>
                  <span>{order.cover_style_name || order.cover_style_id}</span>
                </div>
              </div>
            </div>

            {/* 처리 시각 섹션 */}
            <div className={styles.infoSection}>
              <h2>⏰ 처리 시각</h2>
              <div className={styles.infoList}>
                <div className={styles.infoListItem}>
                  <label>주문일</label>
                  <span>
                    {order.created_at ? new Date(order.created_at).toLocaleString('ko-KR') : '-'}
                  </span>
                </div>
                <div className={styles.infoListItem}>
                  <label>최종 수정일</label>
                  <span>
                    {order.updated_at ? new Date(order.updated_at).toLocaleString('ko-KR') : '-'}
                  </span>
                </div>
                <div className={styles.infoListItem}>
                  <label>관리자 메모</label>
                  <span>{order.admin_note || ' '}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 파티션 (1/4) */}
        <div className={styles.rightPartition}>
          {/* 전자책 컨텐츠 관련 섹션 */}
          <div className={styles.infoSection}>
            <h2>📖 전자책 컨텐츠</h2>
            <div className={styles.ebookActions}>
              {/* <button 
                onClick={handleViewEbook}
                className={`${styles.actionBtn} ${styles.viewBtn}`}
                disabled={!order.book_status || order.book_status === 'DRAFT'}
              >
                📖 전자책 내용 보기
              </button> */}
              <button 
                onClick={handleDownloadEbook}
                className={`${styles.actionBtn} ${styles.downloadBtn}`}
                disabled={!order.book_status || order.book_status === 'DRAFT' || updating}
              >
                {updating ? '⏳ 다운로드 중...' : '📥 전자책 컨텐츠 다운로드'}
              </button>
            </div>
            <div className={styles.ebookStatusInfo}>
              <p>
                <strong>💡 참고:</strong> 전자책이 완성된 상태에서만 컨텐츠 다운로드가 가능합니다.
                {order.book_status === 'DRAFT' && (
                  <span className={styles.warning}> (현재 초안 상태)</span>
                )}
              </p>
            </div>
          </div>

          {/* 관리자 액션 섹션 */}
          <div className={styles.infoSection}>
            <h2>⚙️ 관리자 액션</h2>
            <div className={styles.adminActions}>
              <button 
                onClick={() => setShowStatusModal(true)}
                className={`${styles.actionBtn} ${styles.statusBtn}`}
                disabled={order.delivery_state === 'CANCELLED'}
              >
                🔄 배송 상태 변경
              </button>
              <button 
                onClick={handleOpenCancelModal}
                className={`${styles.actionBtn} ${styles.cancelBtn}`}
                disabled={ order.delivery_state === 'CANCELLED' }
              >
                ❌ 주문 취소
              </button>
              <button 
                onClick={handleOpenDeleteModal}
                className={`${styles.actionBtn} ${styles.cancelBtn}`}
                disabled={updating || order.delivery_state !== 'CANCELLED'}
              >
                🗑️ 주문 삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 상태 변경 모달 */}
      {showStatusModal && (
        <div className={styles.modalOverlay} onClick={() => setShowStatusModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>🔄 배송 상태 변경</h3>
            <div className={styles.modalContent}>
              <div className={styles.formGroup}>
                <label>새로운 배송 상태:</label>
                <select
                  value={newDeliveryState}
                  onChange={(e) => setNewDeliveryState(e.target.value)}
                  className={styles.formSelect}
                >
                  {deliveryStates
                    .filter(state => state.value !== 'CANCELLED')
                    .map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>관리자 메모:</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="상태 변경 사유나 추가 정보를 입력하세요"
                  className={styles.formTextarea}
                  rows={3}
                />
              </div>
            </div>
            <div className={styles.modalActions}>
              <button 
                onClick={handleStatusChange}
                disabled={updating}
                className={styles.confirmBtn}
              >
                {updating ? '⏳ 처리 중...' : '✅ 상태 변경'}
              </button>
              <button 
                onClick={() => setShowStatusModal(false)}
                className={styles.cancelBtn}
              >
                ❌ 취소
              </button>

              <button 
                onClick={() => setShowDeleteModal(true)}
                className={styles.cancelBtn}
              >
                ❌ 삭제
              </button>

            </div>
          </div>
        </div>
      )}

      {/* 주문 취소 모달 */}
      {showCancelModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCancelModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>❌ 주문 취소</h3>
            <div className={styles.modalContent}>
              <p className={styles.warningText}>
                ⚠️ 이 주문을 취소하시겠습니까? 취소된 주문은 복구할 수 없습니다.
              </p>
              
              {/* 패스코드 인증 섹션 */}
              <div className={styles.formGroup}>
                <label>🔐 보안 인증:</label>
                <div className={styles.passcodeSection}>
                  <div className={styles.passcodeDisplay}>
                    <span className={styles.passcodeLabel}>패스코드:</span>
                    <span className={styles.passcodeValue}>{cancelPasscode}</span>
                  </div>
                  <div className={styles.passcodeInput}>
                    <input
                      type="text"
                      value={cancelPasscodeInput}
                      onChange={(e) => setCancelPasscodeInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && cancelPasscodeInput.length === 6) {
                          handleVerifyPasscode();
                        }
                      }}
                      placeholder="위 패스코드를 입력하세요"
                      className={styles.formInput}
                      maxLength={6}
                    />
                    <button 
                      onClick={handleVerifyPasscode}
                      disabled={!cancelPasscodeInput || cancelPasscodeInput.length !== 6}
                      className={styles.verifyBtn}
                    >
                      확인
                    </button>
                  </div>
                  {isPasscodeVerified && (
                    <div className={styles.verifiedMessage}>
                      ✅ 패스코드가 확인되었습니다.
                    </div>
                  )}
                </div>
              </div>
              
              {/* <div className={styles.formGroup}>
                <label>취소 사유:</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="주문 취소 사유를 입력하세요"
                  className={styles.formTextarea}
                  rows={3}
                />
              </div> */}
            </div>
            <div className={styles.modalActions}>
              <button 
                onClick={handleCancelOrder}
                disabled={updating || !isPasscodeVerified}
                className={`${styles.confirmBtn} ${styles.danger} ${!isPasscodeVerified ? styles.disabled : ''}`}
              >
                {updating ? '⏳ 처리 중...' : '❌ 주문 취소'}
              </button>
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  setIsPasscodeVerified(false);
                  setCancelPasscodeInput('');
                }}
                className={styles.cancelBtn}
              >
                ❌ 취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 주문 삭제 모달 */}
      {showDeleteModal && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>❌ 주문 삭제</h3>
            <div className={styles.modalContent}>
              <p className={styles.warningText}>
                ⚠️ 경고: 이 주문을 영구적으로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
              
              {/* 패스코드 인증 섹션 */}
              <div className={styles.formGroup}>
                <label>🔐 보안 인증:</label>
                <div className={styles.passcodeSection}>
                  <div className={styles.passcodeDisplay}>
                    <span className={styles.passcodeLabel}>패스코드:</span>
                    <span className={styles.passcodeValue}>{deletePasscode}</span>
                  </div>
                  <div className={styles.passcodeInput}>
                    <input
                      type="text"
                      value={deletePasscodeInput}
                      onChange={(e) => setDeletePasscodeInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && deletePasscodeInput.length === 6) {
                          handleVerifyDeletePasscode();
                        }
                      }}
                      placeholder="위 패스코드를 입력하세요"
                      className={styles.formInput}
                      maxLength={6}
                    />
                    <button 
                      onClick={handleVerifyDeletePasscode}
                      disabled={!deletePasscodeInput || deletePasscodeInput.length !== 6}
                      className={styles.verifyBtn}
                    >
                      확인
                    </button>
                  </div>
                  {isDeletePasscodeVerified && (
                    <div className={styles.verifiedMessage}>
                      ✅ 패스코드가 확인되었습니다.
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>관리자 메모:</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="삭제 사유를 입력하세요"
                  className={styles.formTextarea}
                  rows={3}
                />
              </div>
            </div>
            <div className={styles.modalActions}>
              <button 
                onClick={handleDeleteDelivery}
                disabled={updating || !isDeletePasscodeVerified}
                className={`${styles.confirmBtn} ${styles.danger} ${!isDeletePasscodeVerified ? styles.disabled : ''}`}
              >
                {updating ? '⏳ 처리 중...' : '❌ 영구 삭제'}
              </button>
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setIsDeletePasscodeVerified(false);
                  setDeletePasscodeInput('');
                }}
                className={styles.cancelBtn}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast 컴포넌트 */}
      {toastIsVisible && toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={toastIsVisible}
          onClose={() => setToastIsVisible(false)}
        />
      )}
    </div>
  );
};

export default DeliveryDetail;
